import React from "react";
import { X } from "lucide-react";
import { type AddAddressModalProps } from "./types";

const AddAddressModal: React.FC<AddAddressModalProps> = ({
  show,
  newAddress,
  isLoading,
  editingAddressId,
  setNewAddress,
  onClose,
  onAddressChange,
  onSubmit,
}) => {
  if (!show) return null;

  const isEditMode =
    editingAddressId !== null && editingAddressId !== undefined;
  const title = isEditMode ? "Edit address" : "Add new address";
  const buttonText = isEditMode ? "Update Address" : "Add Address";

  // Add local state for error message
  const [error, setError] = React.useState<string | null>(null);

  // Validation function
  const validateFields = () => {
    if (
      !newAddress.address_type_value ||
      !newAddress.street ||
      !newAddress.city ||
      !newAddress.building ||
      !newAddress.floor ||
      !newAddress.apartment
    ) {
      setError("Please fill all required fields.");
      return false;
    }
    setError(null);
    return true;
  };

  // Handle submit with validation
  const handleSubmit = () => {
    if (validateFields()) {
      onSubmit();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 rounded-xl max-w-lg w-full max-h-[95vh] overflow-y-auto border border-neutral-800 shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-neutral-800">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Address Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Address Type <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="Address type (e.g., Home, Work)"
              value={newAddress.address_type_value || ""}
              onChange={(e) =>
                setNewAddress({
                  ...newAddress,
                  address_type_value: e.target.value,
                })
              }
              className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          {/* Address Fields */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Street Address <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="Street address"
              value={newAddress.street}
              onChange={(e) => onAddressChange("street", e.target.value)}
              className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              City <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="City"
              value={newAddress.city}
              onChange={(e) => onAddressChange("city", e.target.value)}
              className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Building <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="Building"
                value={newAddress.building}
                onChange={(e) => onAddressChange("building", e.target.value)}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Floor <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="Floor"
                value={newAddress.floor}
                onChange={(e) => onAddressChange("floor", e.target.value)}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Apartment <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="Apartment"
                value={newAddress.apartment}
                onChange={(e) => onAddressChange("apartment", e.target.value)}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Description
            </label>
            <textarea
              placeholder="Additional address details"
              value={newAddress.description}
              onChange={(e) => onAddressChange("description", e.target.value)}
              rows={1}
              className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y overflow-hidden"
            />
          </div>

          {/* Default Address Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={newAddress.is_default}
              onChange={(e) => onAddressChange("is_default", e.target.checked)}
              className="rounded border-neutral-600 bg-neutral-800 text-primary focus:ring-primary"
            />
            <label className="text-sm text-neutral-300">
              Set as default shipping address
            </label>
          </div>

          {/* Show error message */}
          {error && <div className="text-red-400 text-sm">{error}</div>}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex-1 bg-primary text-black py-3 rounded-lg font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Adding..."
                : buttonText}
            </button>
            <button
              onClick={onClose}
              className="flex-1 border border-neutral-700 text-neutral-300 py-3 rounded-lg font-medium hover:bg-neutral-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAddressModal;
